import {
  Bars3Icon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  Cog8ToothIcon,
  EnvelopeIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card } from '@heroui/card';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminStatus } from '../../hooks/useAdminStatus';

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { isAdmin, isLoading } = useAdminStatus();

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      toast.error('Admin access required');
      navigate('/', { replace: true });
    }
  }, [isAdmin, isLoading, navigate]);

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon, current: false },
    {
      name: 'Orders',
      href: '/admin/orders',
      icon: ClipboardDocumentListIcon,
      current: false,
    },
    {
      name: 'Boards',
      href: '/admin/boards',
      icon: ClipboardDocumentListIcon,
      current: false,
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: ChartBarIcon,
      current: false,
    },
    {
      name: 'Customers',
      href: '/admin/customers',
      icon: UsersIcon,
      current: false,
    },
    {
      name: 'Email Settings',
      href: '/admin/email-settings',
      icon: EnvelopeIcon,
      current: false,
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Cog8ToothIcon,
      current: false,
    },
    {
      name: 'Cache Warmup',
      href: '/admin/warmup',
      icon: Cog8ToothIcon,
      current: false,
    },
  ];

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary' />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Card className='p-8 text-center'>
          <h2 className='text-2xl font-bold text-red-600 mb-4'>
            Access Denied
          </h2>
          <p className='text-gray-600 mb-4'>
            You don't have permission to access the admin area.
          </p>
          <Button as={Link} to='/' color='primary'>
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className='fixed inset-0 flex z-40 md:hidden'
          role='dialog'
          aria-modal='true'
        >
          <div
            className='fixed inset-0 bg-gray-600 bg-opacity-75'
            aria-hidden='true'
            onClick={() => setSidebarOpen(false)}
          />
          <div className='relative flex-1 flex flex-col max-w-xs w-full bg-white'>
            <div className='absolute top-0 right-0 -mr-12 pt-2'>
              <button
                type='button'
                className='ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
                onClick={() => setSidebarOpen(false)}
              >
                <span className='sr-only'>Close sidebar</span>
                <XMarkIcon className='h-6 w-6 text-white' aria-hidden='true' />
              </button>
            </div>
            <SidebarContent navigation={navigation} />
          </div>
          <div className='flex-shrink-0 w-14' aria-hidden='true' />
        </div>
      )}

      {/* Desktop sidebar */}
      <div className='hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0'>
        <SidebarContent navigation={navigation} />
      </div>

      {/* Main content */}
      <div className='md:pl-64 flex flex-col'>
        {/* Top navbar for mobile */}
        <div className='sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white shadow'>
          <button
            type='button'
            className='-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500'
            onClick={() => setSidebarOpen(true)}
          >
            <span className='sr-only'>Open sidebar</span>
            <Bars3Icon className='h-6 w-6' aria-hidden='true' />
          </button>
        </div>

        {/* Page content */}
        <main className='flex-1'>
          <div className='py-6'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 md:px-8'>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarContent: React.FC<{ navigation: any[] }> = ({ navigation }) => {
  const { user } = useAuth();
  const { role } = useAdminStatus();

  return (
    <div className='flex flex-col h-0 flex-1 bg-white border-r border-gray-200'>
      {/* Logo and brand */}
      <div className='flex items-center h-16 flex-shrink-0 px-4 bg-primary'>
        <Link to='/admin' className='flex items-center'>
          <h1 className='text-xl font-bold text-white'>Admin Panel</h1>
        </Link>
      </div>

      {/* User info */}
      <div className='p-4 border-b border-gray-200'>
        <div className='flex items-center'>
          <div className='flex-shrink-0'>
            <div className='h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center'>
              <span className='text-sm font-medium text-primary-700'>
                {user?.firstName?.[0] || 'A'}
              </span>
            </div>
          </div>
          <div className='ml-3'>
            <p className='text-sm font-medium text-gray-900'>
              {user?.firstName} {user?.lastName}
            </p>
            <p className='text-xs text-gray-500'>{role}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className='flex-1 px-2 py-4 space-y-1 overflow-y-auto'>
        {navigation.map(item => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/admin'}
              className={({ isActive }) =>
                `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary-50 border-primary-500 text-primary-700 border-r-2'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <Icon className='mr-3 flex-shrink-0 h-5 w-5' aria-hidden='true' />
              {item.name}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className='flex-shrink-0 p-4 border-t border-gray-200'>
        <Link
          to='/'
          className='group flex items-center px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900'
        >
          <HomeIcon className='mr-3 flex-shrink-0 h-5 w-5' aria-hidden='true' />
          Back to Site
        </Link>
      </div>
    </div>
  );
};

export default AdminLayout;
